/**
 * Filtered Order actions renderer
 */
import {
  AbstractType,
  Component,
  InjectionToken,
  Injector,
  Type,
  ViewEncapsulation,
} from '@angular/core';
import { ICellRendererParams } from 'ag-grid-community';
import { AgRendererComponent } from 'ag-grid-angular';
import {
  ActionButton,
  COMMON_ACTION_TYPES,
  ITransferData,
  TRANSFER_PDF_HEADERS,
  TRANSFER_TYPE,
  TRANSFER_PDF_HEADERS_FINAL,
  TRANSFER_PDF_HEADER_VALUES,
} from '@shared/types';
import { Router } from '@angular/router';
import { cloneDeep, forEach, get, includes, isEqual, isFunction } from 'lodash';
import { QohService, StateService, TransferService } from '@shared/services';
import { APIActions, ToolsActions, UiActions } from '@store/actions';

import { PdfBaseService } from '@app/shared/common/make-pdf';
import {
  IPdfDefinitions,
  PdfGenerateServiceV2,
} from '@app/shared/common/make-pdf/v2';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { TransferBulkStatusService } from '@app/shared/services/transfer-bulk/transfer-bulk-status.service';
import { TransferPdfService } from '@app/shared/services/transfer/transfer.pdf.service';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { TRANSFER_ACTION_BUTTONS } from '@app/shared/types/entities/transfer/transfer-action-buttons.enum';
import { TRANSFER_FINAL_STATUS } from '@app/shared/services/transfer/transfer.definition';
import { FilteredTransferService } from '@app/shared/services/filtered-transfer/filtered-transfer.service';
import { addQtyPulledColumn } from '@app/shared/services/transfer/transfer.util';
import { getTotalsDefaultValue } from '@app/shared/services/transfer/transfer-pdf-config';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-filtered-transfer-actions-renderer',
  templateUrl:
    './../transfer-actions-renderer/transfer-actions-renderer.component.html',
  styleUrls: [
    './../transfer-actions-renderer/transfer-actions-renderer.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class XVIFilteredTransferRendererComponent
  implements AgRendererComponent
{
  @lazyInject(PdfBaseService) public pdfBaseService: PdfBaseService;
  @lazyInject(Router) public router: Router;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(ToolsActions) public toolsActions: ToolsActions;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(FilteredTransferService)
  public filteredTransferService: FilteredTransferService;
  @lazyInject(TransferBulkStatusService)
  public transferBulkStatusService: TransferBulkStatusService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(TransferPdfService) public transferPdfService: TransferPdfService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;

  public data: ITransferData;
  public actionButtons: ActionButton[];
  public constructor(public injector: Injector) {}

  public agInit(params: ICellRendererParams): void {
    this.data = params.node.data;
    this.initActionButtons();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public refresh(params: any): boolean {
    const defaultRequestParams = {
      query: {},
      options: {},
    };
    const entityType = this.filteredTransferService.config.ENTITY_TYPE;
    const previousRequest = this.stateService.getByPath(
      ['entities', entityType, 'previousRequest'],
      defaultRequestParams,
    );

    this.apiActions.fetchList(
      entityType,
      previousRequest.query,
      previousRequest.options,
    );

    return true;
  }

  public async edit(forceNotDmSync: boolean = false): Promise<void> {
    const status = this.data?.status;
    const isStatusNeedDmSync = includes(
      this.filteredTransferService.needGetUpdateItemsStatus,
      status,
    )
      ? true
      : null;

    this.router.navigate(
      [this.filteredTransferService.config.uiRoute, get(this, 'data._id', '')],
      {
        queryParams: {
          get_updated_items: forceNotDmSync ? false : isStatusNeedDmSync,
        },
      },
    );
  }

  public async printOrExport(
    type: COMMON_ACTION_TYPES.Print | COMMON_ACTION_TYPES.Export,
  ): Promise<void> {
    const isNotFinalTransfer = this.transferPdfService.isNotFinalTransfer(
      this.data.status,
      this.data.transfer_type,
    );
    let modalColumns = isNotFinalTransfer
      ? [...TRANSFER_PDF_HEADERS]
      : [...TRANSFER_PDF_HEADERS_FINAL];
    modalColumns = await this.filteredTransferService.getModalColumns(
      this.data.site_id,
      modalColumns,
      this.data.status,
    );
    modalColumns = addQtyPulledColumn(modalColumns, {
      transferStatus: this.data.status,
      preColumnPath: isNotFinalTransfer
        ? TRANSFER_PDF_HEADER_VALUES.Quantity
        : TRANSFER_PDF_HEADER_VALUES.InitialQuantity,
    });

    const pdfConfig = await this.filteredTransferService.getPdfConfig(
      (definitions: IPdfDefinitions) =>
        this.filteredTransferService.getPdfData(
          this.data,
          definitions?.options,
        ),
      modalColumns,
      null,
      {
        totalsConfiguration: {
          enabled: true,
          getDefaultValue: () => getTotalsDefaultValue(this.data),
        },
      },
    );
    if (isEqual(type, COMMON_ACTION_TYPES.Export)) {
      await this.pdfGenerateServiceV2.downloadPdfAsync(pdfConfig);
    } else {
      await this.pdfGenerateServiceV2.printPdfAsync(pdfConfig);
    }
  }

  public async voidTransfer(): Promise<void> {
    const updateResult = await this.filteredTransferService.bulkVoidHandler([
      this.data,
    ]);

    if (updateResult) {
      this.refresh({});
    }
  }

  public emitAction(type: string): void {
    switch (type) {
      case COMMON_ACTION_TYPES.View:
        this.edit();
        break;
      case COMMON_ACTION_TYPES.Void:
        this.voidTransfer();
        break;
      case COMMON_ACTION_TYPES.Print:
      case COMMON_ACTION_TYPES.Export:
        this.printOrExport(type);
        break;
      default:
        break;
    }
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  protected initActionButtons(): void {
    const isPreEvent: boolean = isEqual(
      TRANSFER_TYPE.PRE_EVENT_TRANSFER,
      this.data.transfer_type,
    );
    const isOutsideOrDuring: boolean = includes(
      [
        TRANSFER_TYPE.DURING_EVENT_TRANSFER,
        TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
      ],
      this.data.transfer_type,
    );

    this.actionButtons = cloneDeep(TRANSFER_ACTION_BUTTONS) as ActionButton[];

    forEach(this.actionButtons, (btn) => {
      switch (btn.type) {
        case COMMON_ACTION_TYPES.View:
          btn.visible = includes(TRANSFER_FINAL_STATUS, this.data.status);
          break;
        case COMMON_ACTION_TYPES.Print:
        case COMMON_ACTION_TYPES.Export:
          btn.visible =
            includes(
              [
                TRANSFER_STATUS_VALUE.Canceled,
                TRANSFER_STATUS_VALUE.Completed,
                TRANSFER_STATUS_VALUE.Open,
                TRANSFER_STATUS_VALUE.Void,
              ],
              this.data.status,
            ) ||
            isPreEvent ||
            isOutsideOrDuring;
          break;
        case COMMON_ACTION_TYPES.Void:
          btn.visible = this.data.visibleVoidBtn;
          break;
        default:
          break;
      }
    });
  }
}
